import {
	deriveStorefrontInvoicePaymentStatus,
	STOREFRONT_INVOICE_PAYMENT_STATUS_LABELS,
	storefrontInvoiceAwaitsPayment,
	type StorefrontInvoicePaymentStatus,
	type StorefrontInvoiceStatusFieldsModel,
} from '@supply-mover-2-0/core';
import {
	creditMemoRemainingCredit,
	deriveCreditMemoApplicationStatus,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';

/**
 * Invoice row chips shared by every surface that renders an invoice card —
 * the invoices list, the recent-invoices widget, and statement detail rows.
 * Centralized so "what does Paid / Unpaid / Partial look like" (and, with
 * PD-14697, "how urgent is this invoice") stays identical across surfaces.
 */

/**
 * The subset of the invoice list DTO the chips derive their state from. The
 * state itself (Paid / Unpaid / Partial, and since PD-16381 Closed /
 * Cancelled when the invoice's status takes it out of payment) is derived in
 * libs/core, where it is tested.
 */
export type InvoiceChipFieldsModel = StorefrontInvoiceStatusFieldsModel;

/**
 * Closed and Cancelled take the neutral treatment: there is nothing for the
 * customer to act on, so neither the red of money owed nor the green of a
 * settled invoice fits.
 */
const STATUS_CLASSES: Record<StorefrontInvoicePaymentStatus, string> = {
	paid: 'bg-success-badge text-success-tint-fg border border-success-tint',
	unpaid: 'bg-danger-badge text-danger-tint-fg border border-danger-tint',
	partial: 'bg-warning-badge text-warning-tint-fg border border-warning-tint',
	closed: 'bg-brand-input text-strong',
	cancelled: 'bg-brand-input text-strong',
};

/**
 * Payment-status pill (Paid / Unpaid / Partial / Closed / Cancelled). Callers
 * skip it for credit memos — payment status is meaningless there and the
 * "Credit Memo" label already identifies the row.
 */
export function InvoicePaymentStatusPill({
	invoice,
}: {
	invoice: InvoiceChipFieldsModel;
}): JSX.Element {
	const { t } = useTranslation();
	const status = deriveStorefrontInvoicePaymentStatus(invoice);
	return (
		<span
			className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_CLASSES[status]}`}
		>
			{t(STOREFRONT_INVOICE_PAYMENT_STATUS_LABELS[status])}
		</span>
	);
}

/**
 * PD-15441 — the credit still available on a credit memo, as a positive
 * amount. Re-exported through this module so every invoice surface reads a
 * memo's remaining credit the same way (the two producers store its sign
 * differently — see the shared helper).
 */
export function creditMemoAvailable(invoice: InvoiceChipFieldsModel): number {
	return invoice.outstandingBalance == null
		? creditMemoRemainingCredit(invoice.total)
		: creditMemoRemainingCredit(invoice.outstandingBalance);
}

const CREDIT_MEMO_LABEL: Record<'available' | 'partial' | 'used', string> = {
	available: 'Available',
	partial: 'Partially Used',
	used: 'Used',
};

/**
 * A credit memo's states read the OPPOSITE way round from an invoice's:
 * unspent credit is the good news, so `available` takes the success colour
 * and a depleted memo settles to neutral rather than to the green an invoice
 * earns by being paid off. Nothing here is ever danger-coloured — a customer
 * has no problem to fix on a document that owes them money, which is what
 * made the old red "Unpaid" chip on a credit memo read so wrongly.
 */
const CREDIT_MEMO_CLASSES: Record<'available' | 'partial' | 'used', string> = {
	available: 'bg-success-badge text-success-tint-fg border border-success-tint',
	partial: 'bg-warning-badge text-warning-tint-fg border border-warning-tint',
	used: 'bg-brand-input text-strong',
};

/**
 * Credit-memo counterpart to `InvoicePaymentStatusPill` (Available /
 * Partially Used / Used). Callers render this INSTEAD of the payment pill on
 * a credit memo row, so the list, the detail page and the dashboard widget
 * all describe a memo the same way.
 */
export function CreditMemoStatusPill({
	invoice,
}: {
	invoice: InvoiceChipFieldsModel;
}): JSX.Element {
	const { t } = useTranslation();
	const status = deriveCreditMemoApplicationStatus(
		invoice.outstandingBalance,
		invoice.total,
	);
	return (
		<span
			className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${CREDIT_MEMO_CLASSES[status]}`}
		>
			{t(CREDIT_MEMO_LABEL[status])}
		</span>
	);
}

/** Parse the date part of an ISO date / datetime string as a LOCAL date
 *  (avoids the UTC-midnight shift `new Date('YYYY-MM-DD')` would apply). */
function parseDateOnly(value: string): Date | null {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
	if (m == null) {
		return null;
	}
	return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Whole days from today (local midnight) to the given due date.
 *  0 = due today, negative = overdue. Exported for the chip + any surface
 *  that needs the same urgency math. */
export function daysUntilDue(dueDate: string): number | null {
	const due = parseDateOnly(dueDate);
	if (due == null) {
		return null;
	}
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

/** Amber window: due today or within the next 7 days. */
const DUE_SOON_DAYS = 7;

const DUE_CHIP_AMBER =
	'bg-warning-badge text-warning-tint-fg border border-warning-tint';
const DUE_CHIP_RED =
	'bg-danger-badge text-danger-tint-fg border border-danger-tint';
const DUE_CHIP_NEUTRAL = 'bg-brand-input text-strong';

/**
 * Relative due-date chip for unpaid / partially-paid invoices (PD-14697):
 * "Overdue N days" (red), "Due today" / "Due in N days" (amber within a
 * week), or a neutral "Due <date>" for the far future. Renders nothing for
 * paid invoices (the green pill is the whole story), for closed or cancelled
 * ones (nothing is due on a document that can no longer be paid — PD-16381),
 * or when the due date is absent/unparseable — callers can render it
 * unconditionally.
 */
export function InvoiceDueChip({
	invoice,
}: {
	invoice: InvoiceChipFieldsModel & { dueDate: string | null };
}): JSX.Element | null {
	const { t } = useTranslation();
	if (!storefrontInvoiceAwaitsPayment(invoice)) {
		return null;
	}
	if (invoice.dueDate == null || invoice.dueDate === '') {
		return null;
	}
	const days = daysUntilDue(invoice.dueDate);
	if (days == null) {
		return null;
	}

	let classes = DUE_CHIP_NEUTRAL;
	let label: string;
	if (days < 0) {
		classes = DUE_CHIP_RED;
		label =
			days === -1
				? `${t('Overdue')} 1 ${t('day')}`
				: `${t('Overdue')} ${-days} ${t('days')}`;
	} else if (days === 0) {
		classes = DUE_CHIP_AMBER;
		label = t('Due today');
	} else if (days <= DUE_SOON_DAYS) {
		classes = DUE_CHIP_AMBER;
		label =
			days === 1 ? t('Due tomorrow') : `${t('Due in')} ${days} ${t('days')}`;
	} else {
		// Far-future: a neutral date reads better than "Due in 43 days".
		const due = parseDateOnly(invoice.dueDate);
		label = `${t('Due')} ${
			due != null
				? due.toLocaleDateString(undefined, {
						month: 'short',
						day: 'numeric',
					})
				: invoice.dueDate
		}`;
	}

	return (
		<span
			className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${classes}`}
		>
			{label}
		</span>
	);
}
